import { api, atom, injectStore, AtomGetters, injectEffect, injectAtomInstance, injectAtomValue, injectMemo } from '@zedux/react'
import { IUser, ITopic, IContext } from '../types'
import { getTopicUsers, } from '../functions/fetchers'
import { topicsAtom } from './topicsAtom'

const { createPopupHistoryId, hasPopup, registerPopup, requestClose, unregisterPopup } = globalThis.DuscoShared.popupHandler

export const activeTopicHistoryEnabledAtom = atom("active_topic_history_enabled", true)

export const activeTopicAtom = atom("active_topic", (context: IContext) => {
    const store = injectStore<number | null>(null)
    const historyId = injectMemo(() => createPopupHistoryId(`active-topic-${context}`), [context])
    const historyEnabled = injectAtomValue(activeTopicHistoryEnabledAtom)

    const forceClose = () => {
        unregisterPopup(historyId)
        store.setState(null)
    }

    const closeCurrentTopic = () => {
        if (store.getState() === null) {
            forceClose()
            return
        }

        requestClose(historyId, forceClose)
    }

    injectEffect(() => {
        const syncPopupState = (activeTopicId: number | null) => {
            if (!historyEnabled || activeTopicId === null) {
                unregisterPopup(historyId)
                return
            }

            if (!hasPopup(historyId)) {
                registerPopup({ id: historyId, onClose: forceClose })
            }
        }

        syncPopupState(store.getState())

        const sub = store.subscribe((activeTopicId) => {
            syncPopupState(activeTopicId)
        })

        return () => {
            sub.unsubscribe()
            unregisterPopup(historyId)
        }
    }, [historyEnabled, historyId, store])

    return api(store).setExports({ closeCurrentTopic })
})

export const userFilterAtom = atom("userFilter", "")

export const topicIsLoadingAtom = atom("topicIsLoading", true)

export const temporaryTopicAtom = atom("temporary_topic", (context: IContext) => {
    const topicStore = injectStore<ITopic | null>(null)
    const activeTopicInstance = injectAtomInstance(activeTopicAtom, [context])

    injectEffect(() => {
        const state = topicStore.getState()
        if (state) {
            activeTopicInstance.setState(state.topic_id)
            return
        }

        const sub = topicStore.subscribe(newState => {
            if (newState) {
                activeTopicInstance.setState(newState.topic_id)
                // sub.unsubscribe()
            }
        })
        return () => sub.unsubscribe()
    }, [topicStore, activeTopicInstance])

    return topicStore
})

export const loadingTopicsAtom = atom("loading_topics", [] as number[])

export const topicIsLoadingSelector = ({ get }: AtomGetters, topic_id: number) => {
    const loadingTopics = get(loadingTopicsAtom)
    return loadingTopics.includes(topic_id)
}

export const isActiveTopicIsLoadingSelector = ({ get }: AtomGetters, context: IContext) => {
    const activeTopicID = get(activeTopicAtom, [context])
    const loadingTopics = get(loadingTopicsAtom)
    return activeTopicID ? loadingTopics.includes(activeTopicID) : false
}

export const messagesFetchedOnceAtom = atom("messages_fetched_once", [] as number[])

export const topicSelector = ({ get }: AtomGetters, context: IContext, topic_id: number) => {
    const topics = get(topicsAtom, [context])
    const foundTopic = Array.isArray(topics) ? topics.find(t => t?.topic_id === topic_id) : undefined

    if (foundTopic) return foundTopic as ITopic

    const temporaryTopic = get(temporaryTopicAtom, [context])
    if (temporaryTopic?.topic_id === topic_id) {
        return temporaryTopic as ITopic
    }

    return undefined
}


export const currentTopicSelector = (getters: AtomGetters, context: IContext) => {
    const activeTopicID = getters.get(activeTopicAtom, [context])
    if (activeTopicID === null) return undefined
    return topicSelector(getters, context, activeTopicID)
}

export const topicUserAtom = atom("topic_users", (activeTopicID: number) => {
    const topicUsers = injectStore<IUser[]>([], { subscribe: false })
    const promise = getTopicUsers(activeTopicID).then((async (users) => {
        topicUsers.setState(users)
    }))
    return api(topicUsers).setPromise(promise)
})
